import { useEffect, useState, useCallback } from 'react';
import ReactPlayer from 'react-player';
import viinexService from '@/services/viinex';

const STREAM_WINDOW_MILLISECONDS = 60 * 1000;
const STREAM_REFRESH_INTERVAL_MILLISECONDS = 30 * 1000;

enum LiveState {
    Idle = 'idle',
    Fetching = 'fetching',
    Loading = 'loading',
    Playing = 'playing',
    Error = 'error',
}

interface LiveThumbnailProps {
    sourceId: string;
    isHovered: boolean;
    onReady?: () => void;
    onError: () => void;
}

export function LiveThumbnail({
    sourceId,
    isHovered,
    onReady,
    onError,
}: LiveThumbnailProps) {
    const [streamUrl, setStreamUrl] = useState<string | null>(null);
    const [liveState, setLiveState] = useState<LiveState>(LiveState.Idle);

    useEffect(() => {
        if (isHovered && liveState === LiveState.Idle) {
            setLiveState(LiveState.Fetching);
        }
    }, [isHovered, liveState]);

    useEffect(() => {
        if (liveState !== LiveState.Fetching) return;

        const fetchStreamUrl = async () => {
            try {
                const { storageService, boundaries } =
                    await viinexService.fetchStorageData(sourceId);

                const beginMilliseconds = Math.max(
                    boundaries.latestMilliseconds - STREAM_WINDOW_MILLISECONDS,
                    boundaries.earliestMilliseconds,
                );

                const url = viinexService.buildHlsUrl(
                    storageService,
                    sourceId,
                    beginMilliseconds,
                    boundaries.latestMilliseconds,
                );

                setStreamUrl(url);
                setLiveState(LiveState.Loading);
            } catch {
                setLiveState(LiveState.Error);
                onError();
            }
        };

        fetchStreamUrl();
    }, [liveState, sourceId, onError]);

    const handleReady = useCallback(() => {
        setLiveState(LiveState.Playing);
        onReady?.();
    }, [onReady]);

    const handleError = useCallback(() => {
        setLiveState(LiveState.Error);
        onError();
    }, [onError]);

    useEffect(() => {
        if (liveState !== LiveState.Playing) return;

        const refreshStreamUrl = async () => {
            try {
                const { storageService, boundaries } =
                    await viinexService.fetchStorageData(sourceId);

                const beginMilliseconds = Math.max(
                    boundaries.latestMilliseconds - STREAM_WINDOW_MILLISECONDS,
                    boundaries.earliestMilliseconds,
                );

                const newUrl = viinexService.buildHlsUrl(
                    storageService,
                    sourceId,
                    beginMilliseconds,
                    boundaries.latestMilliseconds,
                );

                setStreamUrl(newUrl);
            } catch {
                console.warn('Failed to refresh stream URL:', sourceId);
            }
        };

        const intervalId = setInterval(
            refreshStreamUrl,
            STREAM_REFRESH_INTERVAL_MILLISECONDS,
        );

        return () => clearInterval(intervalId);
    }, [liveState, sourceId]);

    if (!streamUrl || liveState === LiveState.Error) return null;

    const shouldPlay =
        liveState === LiveState.Loading || liveState === LiveState.Playing;

    return (
        <ReactPlayer
            src={streamUrl}
            playing={shouldPlay}
            muted
            height="100%"
            onReady={handleReady}
            onError={handleError}
            config={{ hls: { enableWorker: true, lowLatencyMode: false } }}
            className="pointer-events-none absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 min-w-full min-h-full"
        />
    );
}
